import { CmsPageNotFoundError } from '@eneco/cms-adapter';
import type { CmsAdapter, CmsPage, CmsRendering } from '@eneco/cms-adapter';
import type { Locale } from '@eneco/cms-types';
import { stegaClean } from '@sanity/client/stega';

import { collectEditFields } from './edit-fields';
import { createLinkResolver } from './links';
import { createToImage, toRichText } from './mappers';
import {
  buildAddressPageQuery,
  buildPageQuery,
  CHROME_SLOT_FIELD,
  DEFAULT_BLOCK_PROJECTIONS,
  FLOW_ROOT_LINK_FIELDS,
  HOME_PAGE_SLUG,
  PLACEHOLDER_FIELDS,
  SITE_WIDE_CONTENT_FIELD,
} from './queries';
import { createFetchQueryRunner, type SanityQueryRunner } from './query-runner';
import { createReferenceFiller } from './references';
import { serializeRichText } from './rich-text';
import type { SanityBlock, SanityPage } from './sanity-types';
import { unsweepFamilies } from './unsweep';

export interface SanityAdapterConfig {
  projectId: string;
  dataset: string;
  /** Sanity API version, e.g. `2024-01-01`. */
  apiVersion?: string;
  /** Read token for private datasets. Omit for public datasets. */
  token?: string;
  /** Use the CDN host (`apicdn`) for cached reads. Defaults to true. */
  useCdn?: boolean;
  /**
   * Override the API base host. When set, takes precedence over the derived
   * `https://<projectId>.api(cdn).sanity.io` host. Use for a local dev proxy,
   * a mock server, or a region-specific Sanity host. Read from
   * `env('SANITY_API_HOST')` in the container's cms-config.ts.
   */
  apiHost?: string;
  /** Injectable for tests — defaults to global `fetch`. */
  fetchImpl?: typeof fetch;
  /**
   * Transport for GROQ queries. Defaults to the dependency-free fetch runner.
   * Production injects a `@sanity/client`-backed runner (server-only); tests
   * inject a stub. See {@link SanityQueryRunner}.
   */
  queryRunner?: SanityQueryRunner;

  /**
   * Page document `_type`(s). Default `'page'` — the greenfield POC schema's single page type.
   *
   * A migrated dataset has NO `page` type: every Sitecore page template compiles to its own
   * document type (`enecoArticlePage`, `enecoCoUkStandardContentPage`, …), 189 of them.
   *
   * **Prefer {@link site} over this.** Maintaining the list per container in an env var has cost us
   * a blank page three times over (an unreachable `['page']` default, then a missing
   * `enecoCoUkHomePage`), because a type absent from the list is filtered out before slug matching
   * and the miss surfaces only as an empty render. `site` needs no list and no upkeep.
   *
   * Kept for the greenfield POC dataset, whose documents are all `_type: 'page'` and carry no
   * `site` reference. Ignored when `site` is set.
   */
  documentType?: string | readonly string[];
  /**
   * Scope the lookup to one site's pages via `site._ref == $site` (e.g. `'site-eneco-co-uk'`).
   *
   * The durable alternative to {@link documentType}: every migrated page document carries a `site`
   * reference (ADR-0052) — 204 of 204 in the `development` dataset, across four sites — and a
   * container maps to exactly one, so new page types need no config change. Read from
   * `env('SANITY_SITE')` in the container's `cms-config.ts`.
   *
   * When set, the `_type` filter is dropped unless `documentType` was also given explicitly.
   */
  site?: string;
  /**
   * The container's Next base path (e.g. `/mkb`), stripped from a request path
   * before slug resolution.
   *
   * `getSSP` prepends it because Sitecore's `item=` lookup wants the full item
   * path. Sanity slugs carry no container prefix, so leaving it on makes every
   * lookup miss. Pass `getContainerBasePath()` from `@eneco/next/server`, which
   * is the same source `getSSP` uses, so the two cannot drift.
   *
   * Only `gb-eneco-main` has worked on this lane so far, and it has no base
   * path, which is why this went unnoticed.
   */
  basePath?: string;
  /**
   * Field holding the language code. Default `'language'` (Sanity Document
   * Internationalization plugin), matched against the 2-letter code from the
   * request `Locale` (see {@link toLanguageCandidates}).
   */
  languageField?: string;
  /**
   * Array field(s) selected on the *page document itself* so the query returns them at all — e.g.
   * `sections`. Default {@link PLACEHOLDER_FIELDS}. Only matters at this top level: a nested slot field
   * (a block's own child-placeholder array, e.g. `Section`'s `sectionMain`) needs no entry here — once
   * its parent block is selected, `toRendering` recognizes it as a slot from its *content* shape (every
   * item has a non-`'block'` `_type` and no `_ref`), not from a name list. See `toRendering`'s doc
   * comment.
   */
  slotFields?: readonly string[];
  /**
   * Map a request `Locale` to the language value(s) a document might carry.
   *
   * Default: both spellings, full locale first — `nl-NL` matches a document stored as `nl-NL` or
   * as `nl`. The estate genuinely uses both, and on purpose: the migration's own language list has
   * `nl-NL` for the Netherlands beside a bare `nl` for Belgium's Dutch, `en-GB` for the UK beside a
   * bare `en`, because the codes "are market-specific and carry intent" (ADR-0051/0052). Asking for
   * either spelling means no container has to declare which one its dataset happens to use — a
   * setting whose only failure mode was a silently blank page.
   *
   * Every lookup is already scoped by `site._ref`, and no site declares both spellings of the same
   * language, so accepting both cannot match another market's content.
   *
   * Override only to pin an exact value; returning one entry restores the old behaviour.
   */
  toLanguageCandidates?: (locale: Locale) => readonly string[];
  /**
   * Per-`_type` GROQ projection overrides for reference/asset resolution
   * (eager hydration). Default {@link DEFAULT_BLOCK_PROJECTIONS}.
   */
  blockProjections?: Record<string, string>;
  /**
   * Absolute URL of the Sanity Studio, from `env('SANITY_STUDIO_URL')`. Required for
   * draft mode: it is both where stega-encoded strings point and where a click-to-edit
   * overlay navigates. Reaches the browser on {@link CmsEditingContext}, never as a
   * `NEXT_PUBLIC_` variable — containers are built once and run on every tier, so a
   * build-time inline would bake in an empty string (`config/next/config.js`).
   */
  studioUrl?: string;
  /** Path the render host's "leave draft mode" control posts to. */
  disableDraftUrl?: string;
  /**
   * Studio workspace name, e.g. `acceptance`. Defaults to {@link dataset}, which is correct
   * for a Studio whose workspaces are named after the dataset they bind — Eneco's are. Set
   * explicitly when they diverge; without it, click-to-edit links have no workspace segment
   * and the Studio cannot resolve them.
   */
  studioWorkspace?: string;
  /**
   * Also try the pre-ADR-0077 lookup, where `slug.current` held a page's whole path rather than one
   * segment, when the address lookup finds nothing. Default `true`.
   *
   * Both models exist in the estate at once: the address model is what the Studio now writes, but a
   * dataset migrated before it still carries full-path slugs, and the two cannot be told apart from
   * a request. Costs one extra query only on a miss — which, once a site's content is re-migrated,
   * is a genuine 404. Set `false` per container to make that explicit and drop the second query.
   */
  legacySlugFallback?: boolean;
  /**
   * This environment's own origin, such as `https://www.acc.eneco.nl`, from `env('HOST')`. A link to a
   * page on another site is absolute on it, with the domain swapped for another domain's page, as
   * Sitecore's links are (`links.ts`). Unset, or on a host outside the site's domain (a local or hosted
   * PR build), such a link points at production's `https://www.` address of that site.
   */
  host?: string;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * Fields stripped from a block before it becomes flat `content`. `area`/`parentKey` are structural
 * bookkeeping for the flat-pool reconstruction below (`buildTree`), never real content — see its doc
 * comment for what they mean.
 */
const RESERVED = new Set(['_type', '_key', '_id', '_rev', '_createdAt', '_updatedAt', '_context', 'area', 'parentKey']);

/**
 * `_root1`, `_root2`, …, `_flowName1`, `_flowName2`, … and `_flowCompletionUrl1`, `_flowCancelationUrl1`, …:
 * the ancestor `frontEndRootPath` flags, `flowName`s and flow links `buildAddressPageQuery` projects.
 *
 * Read once by `toFlowRootPath`, `toFlowName` and `toFlowRootLinks` and meaningless to anything
 * downstream, so they are dropped rather than passed on: left in, they would arrive at every component
 * as page content, which is how a projection detail becomes an accidental part of the contract.
 */
const FLOW_ROOT_PROJECTION = new RegExp(`^_(root|flowName|${FLOW_ROOT_LINK_FIELDS.join('|')})\\d+$`);
const isFlowRootProjection = (key: string): boolean => FLOW_ROOT_PROJECTION.test(key);

/**
 * Strip stega characters from a value that is about to be *compared* rather than displayed.
 *
 * `stega-filter.ts` already tells `@sanity/client` never to encode `parentKey` or `area`,
 * which is the real fix. This is the belt to that braces, and it is here because the
 * failure it prevents is uniquely nasty: the page still renders, the tree is silently
 * wrong, and it only happens in draft mode. Cheap enough (a regex over two short strings
 * per block) to not be worth reasoning about every time the filter config is touched.
 */
const asKey = (value: unknown): string | undefined =>
  typeof value === 'string' ? stegaClean(value) : undefined;

/** What a name and label entry may hold: its bookkeeping, a `name`, and at most a `label` and a `description`. */
const NAME_LABEL_MEMBERS: ReadonlySet<string> = new Set(['_key', '_type', 'name', 'label', 'description']);

/**
 * An entry of a list that is data rather than a child rendering. The migration writes two kinds, and
 * the data marks both:
 *
 * - **A child item** (ADR-0083): a Sitecore item stored under its parent, such as a choice step's
 *   options in `items`. It carries the item's id as `sitecoreId`, which no section block does
 *   (0 of the 70,705 blocks in `development` on 2026-09-30).
 * - **A name and label entry**: one entry of a Sitecore name=label list, such as a flow navigation's
 *   `navigationSteps` or a hero's `uspList`. It holds a `name` and at most a `label` and a
 *   `description`, where a block always has fields of its own.
 *
 * Both are told apart by shape, not by their type names, so a new list needs no change here.
 */
const isListEntry = (item: Record<string, unknown>): boolean =>
  typeof item.sitecoreId === 'string' ||
  (typeof item.name === 'string' && Object.keys(item).every(key => NAME_LABEL_MEMBERS.has(key)));

/**
 * An array is a nested placeholder slot when every item is a genuine block: an object with a `_type`
 * that isn't Portable Text (`'block'`) and isn't a Sanity reference (`_ref`) — those are the only two
 * shapes a slot array could be confused with (rich text and reference lists), and neither is ever a
 * registered `_type` on the eneco side. An empty array carries no shape to go on, so it defaults to
 * `content` — harmless either way, since `<Placeholder>`/`usePlaceholder` already treat a missing key as
 * no children.
 *
 * This replaces name-based slot detection (a field name allowlist) for anything *nested* — the migration
 * bundle generates ~500 component types with their own slot fields (`sectionMain`, `splitViewFirst`, …),
 * far more than a hand-maintained name list could track without silently going stale. It intentionally
 * does not touch `SanityAdapterConfig.slotFields`, which still selects the *page document's own* content
 * field(s) for the query to fetch at all (see `queries.ts#buildPageQuery`) — a name list is unavoidable
 * there, but it's one or two names, not one per component type.
 *
 * Shape inference is a bridge for a generator-produced schema, not the end state: a hand-authored Sanity
 * project normally just writes each type's query explicitly (as `DEFAULT_BLOCK_PROJECTIONS` already does
 * per-`_type` for reference resolution) because the schema author and the query author are the same
 * person. Once this migration's schema stabilizes, migrating slot knowledge back to the schema itself
 * (ideally a marker the generator emits, since it already knows a field came from a Sitecore placeholder
 * — see each generated section file's own comments) is the more idiomatic long-term shape.
 *
 * This is one of two ways a block's children reach `buildTree` — see that function's doc comment for the
 * other (and far more common, in the actual migrated content): a flat pool correlated by `parentKey`/
 * `area` rather than genuine field nesting. Both are supported because nothing here can assume a given
 * dataset uses only one; a schema's *declared* shape (this generator's own TS output nests fields like
 * `sectionMain`) does not guarantee its *stored* content does too.
 *
 * A family entry (ADR-0068: `notifications`, `radioGroups`, `richText`, …) is never a slot, and is
 * told apart by its `role`, which no section type declares. Now that the migration writes the `_type`
 * the generated types describe, that check is what keeps every family out of the child renderings and
 * in reach of `unsweepFamilies`.
 *
 * Nor is a list entry (see `isListEntry`). Before that check, every choice step's options and every
 * flow navigation's steps left `content` as child renderings that nothing renders: in the
 * `development` dataset of 2026-09-30 the rule above claimed 3,883 lists, and every one of their
 * 15,973 entries was a child item or a name and label entry.
 *
 * Nor is a document. A followed reference arrives as the document it points at, `_id` and all, and no
 * block carries an `_id` (0 of the 72,134 blocks in that dataset). The migrated documents also carry a
 * `sitecoreId`, but a document written in the Studio does not.
 */
const isSlotArray = (value: unknown): value is SanityBlock[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    item =>
      isObject(item) &&
      typeof item._type === 'string' &&
      item._type !== 'block' &&
      !('_ref' in item) &&
      !('_id' in item) &&
      !('role' in item) &&
      !isListEntry(item),
  );

/**
 * GROQ path helpers for Visual Editing's `data-sanity` attributes.
 *
 * A path addresses a field inside a document the way the Studio does:
 * `sections[_key=="a1b2"].sectionMain`. `joinPath` appends a field name,
 * `itemPath` narrows to one array entry by its `_key`.
 *
 * A block with no `_key` (a singleton object field rather than an array entry)
 * cannot be addressed individually, so `itemPath` returns the array's own path —
 * the overlay then targets the array, which is the truthful answer.
 */
const joinPath = (parent: string, field: string): string => (parent ? `${parent}.${field}` : field);

const itemPath = (arrayPath: string | undefined, item: SanityBlock): string | undefined => {
  if (arrayPath === undefined) return undefined;
  return typeof item._key === 'string' && item._key ? `${arrayPath}[_key=="${item._key}"]` : arrayPath;
};

/**
 * A block's fields with the shared document it points at filled in (ADR-0063).
 *
 * The schema bundle sets `siteWideContent` instead of a block's own fields when the content is
 * shared across the site, and the document it references holds that content under the block's own
 * field names: a shared cookie wall's `title` is the cookie wall's `title`. Offering those fields as
 * the block's own means a component reads them the same way from either CMS. Sitecore already sends
 * a shared datasource's fields as the rendering's fields, wherever the item lives.
 *
 * The block's own value wins wherever it has one, so nothing the block states is hidden, and
 * `siteWideContent` itself stays for the components that read the shared document directly. Only a
 * resolved document has fields to offer: a reference the query could not follow arrives as `null`
 * and is dropped before this, and an unresolved one carries no `_id`.
 */
const withSiteWideContent = (fields: Record<string, unknown>): Record<string, unknown> => {
  const shared = fields[SITE_WIDE_CONTENT_FIELD];
  if (!isObject(shared) || typeof shared._id !== 'string') return fields;

  const offered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(shared)) {
    if (RESERVED.has(key) || value === null || value === undefined) continue;
    offered[key] = value;
  }
  return { ...offered, ...fields };
};

/**
 * A block's fields as a component reads them: its shared document's fields filled in, swept fields
 * back under their own names, then rich text as HTML. In that order, because the un-sweep is what
 * brings a notification's body out of its family list, where `serializeRichText` can reach it, and
 * a shared document's fields need both.
 */
const toComponentContent = (fields: Record<string, unknown>): Record<string, unknown> =>
  serializeRichText(unsweepFamilies(withSiteWideContent(fields)));

/**
 * A rendering the `@eneco/cms` placeholder already mapped, handed back by a bridge component.
 *
 * The placeholder gives a registered component its rendering as props: the mapped content as `fields`,
 * with `params`, `uid` and `componentName` (`libs/cms/src/placeholder.tsx`). A bridge turns those props
 * back into a rendering through `adapter.toRendering`. The Sitecore adapter leaves flat content as it
 * is, so that works on its lane by construction. Read here as a Sanity block, the content landed under
 * `content.fields` and the component got none of its fields. The two are told apart by shape: a Sanity
 * block always carries `_type`, and a mapped rendering never does but names its component.
 */
interface MappedRendering {
  componentName: string;
  uid?: unknown;
  fields?: unknown;
  params?: unknown;
}

const isMappedRendering = (raw: unknown): raw is MappedRendering =>
  isObject(raw) && typeof raw._type !== 'string' && typeof raw.componentName === 'string';

/** A {@link MappedRendering} as the rendering it already is, its content untouched. */
const fromMappedRendering = ({ componentName, uid, fields, params }: MappedRendering): CmsRendering => ({
  componentName,
  ...(typeof uid === 'string' ? { uid } : {}),
  content: isObject(fields) ? fields : {},
  ...(isObject(params) ? { params: params as CmsRendering['params'] } : {}),
});

/**
 * Map a request URL path to the slug values a Sanity `slug.current` might hold.
 * Strips any query string and tolerates leading/trailing-slash conventions, so
 * `/home/?x=1` matches a doc whose slug is `home`, `/home`, or `/home/`.
 */
export const toSlugCandidates = (path: string): string[] => {
  const noQuery = path.split('?')[0];
  const bare = noQuery.replace(/^\/+/, '').replace(/\/+$/, '');
  if (bare === '') return ['/'];
  return Array.from(new Set([noQuery, `/${bare}`, bare, `/${bare}/`]));
};

/**
 * Remove the container's own base path from a request path.
 *
 * See {@link SanityAdapterConfig.basePath}. A path equal to the prefix is the
 * container's home page, which a migrated dataset stores as the literal slug
 * `/`, so it maps to `/` rather than to the empty string.
 *
 * Returns a pathname: any query string is dropped first, because comparing the
 * raw path would leave `/mkb?x=1` unmatched and the home page unresolvable.
 * Every caller derives slugs or segments from the result and strips queries
 * anyway, so this normalises once rather than at each of them. `resolveCmsPage`
 * happens to clean its own input, but a direct `CmsClient` caller does not.
 */
/** A base path as a leading prefix: `/mkb` from `mkb`, `/mkb/` or `//mkb//`, and `/` from nothing. */
const toBasePathPrefix = (basePath: string): string => `/${basePath.replace(/^\/+/, '').replace(/\/+$/, '')}`;

export const stripBasePath = (path: string, basePath?: string): string => {
  const pathname = path.split('?')[0];
  if (!basePath) return pathname;
  const prefix = toBasePathPrefix(basePath);
  if (prefix === '/') return pathname;
  if (pathname === prefix) return '/';
  return pathname.startsWith(`${prefix}/`) ? pathname.slice(prefix.length) : pathname;
};

/**
 * Path segments, query string and empties removed. `/wetgevingskompas/omzet/`
 * becomes `['wetgevingskompas', 'omzet']`.
 */
const toSegments = (path: string): string[] => path.split('?')[0].split('/').filter(Boolean);

/**
 * Concrete Sanity implementation of the `CmsAdapter` contract.
 *
 * The ONLY package allowed to know about Sanity/GROQ. ESLint enforces this via
 * `lib:cms-sanity`. Mapping is near-identity because the GROQ projection
 * (see `queries.ts`) shapes named slots and inlines the references it names,
 * and `references.ts` fills in every other reference before the page is mapped.
 */
export const createSanityAdapter = (config: SanityAdapterConfig): CmsAdapter => {
  const toImage = createToImage({ projectId: config.projectId, dataset: config.dataset });
  const runner = config.queryRunner ?? createFetchQueryRunner(config);

  // Normalized to a list so the query is one shape (`_type in $documentTypes`) regardless of whether
  // the caller passed one type or many.
  // `site` scoping supersedes the type list, so only fall back to the POC's `page` default when
  // neither is configured — otherwise a container that set `site` would still be narrowed to a type
  // its dataset does not have.
  const documentTypes = config.documentType === undefined ? ['page'] : [config.documentType].flat();
  const matchDocumentTypes = config.documentType !== undefined || config.site === undefined;
  const matchSite = config.site !== undefined;
  const languageField = config.languageField ?? 'language';
  const slotFields = config.slotFields ?? PLACEHOLDER_FIELDS;
  const toLanguageCandidates =
    config.toLanguageCandidates ??
    ((locale: Locale): readonly string[] => [...new Set([locale, locale.split('-')[0]])]);
  const blockProjections = config.blockProjections ?? DEFAULT_BLOCK_PROJECTIONS;
  const pageQuery = buildPageQuery({ languageField, slotFields, blockProjections, matchDocumentTypes, matchSite });
  /** Same projection with the `_type` filter forced on, for the nested lookup in `findPage`. */
  const pinnedTypePageQuery = buildPageQuery({
    languageField,
    slotFields,
    blockProjections,
    matchDocumentTypes: true,
    matchSite,
  });
  /** `_type` of the flow root at `$rootSlug`, or null when that segment names no root. */
  const flowRootTypeQuery = `*[site._ref == $site && frontEndRootPath == true && slug.current == $rootSlug][0]._type`;
  const fillReferences = createReferenceFiller(runner, slotFields);
  const resolveLinks = createLinkResolver(runner, languageField);

  /**
   * A resolved page and the provenance of the resolution.
   *
   * `segments` is the request's own path, root first, and is set only by the address lookup — it is
   * how the flow root's path gets composed without re-parsing the URL. `flowRootSlug` is the legacy
   * lookup's equivalent, set only when the leading segment resolved a flow root.
   */
  type PageResolution = { page: SanityPage; segments?: readonly string[]; flowRootSlug?: string };

  /**
   * The address query for a given depth, built once.
   *
   * Unlike the stored-path query this one depends on the request — it has a join per segment — so
   * it cannot be built at construction time. It can still be built once *per depth*: the projection
   * it wraps carries every slot and every block override, which is a few kilobytes of string to
   * reassemble on a request that is already doing a network round trip. Depths in the estate run
   * 0-6, so this settles at a handful of entries.
   */
  const addressQueries = new Map<number, string>();
  const addressQueryFor = (segmentCount: number): string => {
    const cached = addressQueries.get(segmentCount);
    if (cached !== undefined) return cached;
    const query = buildAddressPageQuery({
      languageField,
      slotFields,
      blockProjections,
      matchDocumentTypes,
      matchSite,
      segmentCount,
    });
    addressQueries.set(segmentCount, query);
    return query;
  };

  /**
   * Where the render host's "leave draft mode" link points, when a container names no path.
   *
   * It has to carry the container's base path. Next mounts API routes under `basePath` too, so
   * mkb's route is `/mkb/api/sanity/draft/disable` — and `basePath` only rewrites `next/link` and
   * the router, not the plain `<a href>` this renders as. Reported by review on PR #10935: the
   * default was unprefixed, so the link 404'd on every container with a base path.
   */
  const defaultDisableDraftUrl = (): string => {
    const prefix = config.basePath ? toBasePathPrefix(config.basePath) : '';
    return `${prefix === '/' ? '' : prefix}/api/sanity/draft/disable`;
  };

  /** GROQ params naming each segment, nearest first — `$seg0` is the last segment of the URL. */
  const segmentParams = (segments: readonly string[]): Record<string, string> =>
    Object.fromEntries([...segments].reverse().map((segment, index) => [`seg${index}`, segment]));

  /**
   * Resolve a request path to a page document.
   *
   * **The address lookup (ADR-0077) is the model.** A page stores one segment in `slug.current`
   * plus a `parentPage` reference, so `/klantenservice/jaarnota/` is a chain of documents and the
   * address only exists once someone walks it. `buildAddressPageQuery` walks it inside GROQ, to the
   * exact depth this request needs, and terminates on the home sentinel — which is what makes a
   * partial match fail rather than resolve to a different page that happens to end in `jaarnota`.
   * Leaf slugs are emphatically not unique: `bedankt` occurs eight times under MKB alone.
   *
   * **The legacy lookup is a fallback, not a peer.** Before ADR-0077 `slug.current` held the whole
   * path. A dataset migrated before the change still does, and nothing in a request distinguishes
   * the two, so a miss falls through to the old query rather than 404-ing content that is merely
   * older than the model. Disable per container with `legacySlugFallback: false` once its content
   * has been re-migrated; see the flag's own comment.
   *
   * The page found comes with the documents its blocks point at filled in (`references.ts`).
   */
  const findPage = async (
    path: string,
    languageCandidates: readonly string[],
    locale: Locale,
    draft = false,
  ): Promise<PageResolution | null> => {
    const requestPath = stripBasePath(path, config.basePath);
    // Every query in this resolution runs at the same perspective. Mixing them would let a
    // draft-only page resolve against a published flow root, or vice versa.
    const options = { draft };
    const segments = toSegments(requestPath);
    const withReferences = async (resolution: PageResolution): Promise<PageResolution> => ({
      ...resolution,
      page: await fillReferences(resolution.page, options),
    });

    const byAddress = await runner.query<SanityPage | null>(
      addressQueryFor(segments.length),
      {
        documentTypes,
        site: config.site ?? null,
        languageCandidates,
        locale,
        homeSlug: HOME_PAGE_SLUG,
        pagePath: requestPath,
        ...segmentParams(segments),
      },
      options,
    );
    if (byAddress) return withReferences({ page: byAddress, segments });

    if (config.legacySlugFallback === false) return null;
    const byStoredPath = await findPageByStoredPath(requestPath, languageCandidates, locale, options);
    return byStoredPath && withReferences(byStoredPath);
  };

  /**
   * The pre-ADR-0077 lookup, unchanged in behaviour: `slug.current` holds the page's whole path.
   *
   * A nested path needs a discriminator, because leaf slugs are not unique. A flow supplies one:
   * its root marks itself with `frontEndRootPath == true` and shares its `_type` with every step,
   * so the leading segment resolves a root whose `_type` pins the leaf. This only ever handled a
   * two-segment flow — the address lookup above handles any depth and supersedes it.
   */
  const findPageByStoredPath = async (
    requestPath: string,
    languageCandidates: readonly string[],
    locale: Locale,
    options: { draft: boolean },
  ): Promise<PageResolution | null> => {
    const direct = await runner.query<SanityPage | null>(
      pageQuery,
      {
        documentTypes,
        site: config.site ?? null,
        slugCandidates: toSlugCandidates(requestPath),
        languageCandidates,
        locale,
      },
      options,
    );
    if (direct) return { page: direct };

    const segments = toSegments(requestPath);
    if (segments.length < 2 || !matchSite) return null;

    const rootType = await runner.query<string | null>(
      flowRootTypeQuery,
      {
        site: config.site ?? null,
        rootSlug: segments[0],
      },
      options,
    );
    if (!rootType) return null;
    // A caller that named its own document types meant it. Pinning the leaf to a
    // root type outside that allow-list would serve a page type they explicitly
    // excluded, which the direct query above would have filtered out.
    if (config.documentType !== undefined && !documentTypes.includes(rootType)) return null;

    const leaf = await runner.query<SanityPage | null>(
      pinnedTypePageQuery,
      {
        documentTypes: [rootType],
        site: config.site ?? null,
        slugCandidates: toSlugCandidates(segments.at(-1) ?? ''),
        languageCandidates,
        locale,
      },
      options,
    );

    // Only this branch proves the leading segment named a flow root. A direct hit
    // proves nothing of the kind, however nested its path looks.
    return leaf ? { page: leaf, flowRootSlug: segments[0] } : null;
  };

  /**
   * How many ancestors up a page's flow root is, under the address lookup: 0 when the page is the
   * root itself, `undefined` when nothing on its chain is one. The nearest flagged page wins.
   *
   * Hop N is the flag `buildAddressPageQuery` projects N ancestors up. Hop 0 is the page's own
   * `frontEndRootPath`, which the projection's `...` already returns, so the query does not repeat it.
   */
  const flowRootHop = (page: SanityPage, segments: readonly string[]): number | undefined => {
    const flagAt = (hop: number): unknown =>
      hop === 0 ? page.frontEndRootPath : (page as Record<string, unknown>)[`_root${hop}`];
    for (let hop = 0; hop <= segments.length; hop += 1) {
      if (flagAt(hop) === true) return hop;
    }
    return undefined;
  };

  /**
   * The name of the flow a page belongs to: its own `flowName`, else its flow root's.
   *
   * Sitecore sends `flowName` on every page of a flow; the migration stores it on the root only. A
   * flow keys its visitor's answers by that name (`useFlowBasket` in `libs/eneco/flows`), so a step
   * opened directly, or reloaded, would keep them under another name than the root did, and lose
   * them. The root's is read from the same ancestor `toFlowRootPath` takes the address from.
   *
   * Only under the address lookup: the legacy one resolves no ancestors to read a name from.
   */
  const toFlowName = ({ page, segments }: PageResolution): string | undefined => {
    const own = asKey(page.flowName);
    if (own) return own;
    if (!segments) return undefined;
    const hop = flowRootHop(page, segments);
    if (hop === undefined || hop === 0) return undefined;
    const name = asKey((page as Record<string, unknown>)[`_flowName${hop}`]);
    return name || undefined;
  };

  /**
   * The flow root's completion and cancel links, for a step that has none of its own.
   *
   * Sitecore sends `flowCompletionUrl` and `flowCancelationUrl` on every page of a flow; the
   * migration stores them on the root only. A flow completes on a step (the SME flow on its summary,
   * once the order is placed) and the router sends the visitor to the completion link of the page it
   * is on (`FlowRouter` in `libs/eneco/flows`), so without its root's a step went nowhere. Taken from
   * the same ancestor `toFlowRootPath` takes the address from, and left as stored: `resolveLinks`
   * converts them with every other link on the page.
   *
   * Only under the address lookup, like the name.
   */
  const toFlowRootLinks = ({ page, segments }: PageResolution): Record<string, unknown> => {
    if (!segments) return {};
    const hop = flowRootHop(page, segments);
    if (hop === undefined || hop === 0) return {};
    const fields = page as Record<string, unknown>;
    const isAbsent = (value: unknown): boolean => value === null || value === undefined;
    const links: Record<string, unknown> = {};
    for (const field of FLOW_ROOT_LINK_FIELDS) {
      const fromRoot = fields[`_${field}${hop}`];
      if (isAbsent(fields[field]) && !isAbsent(fromRoot)) links[field] = fromRoot;
    }
    return links;
  };

  /**
   * The path a flow routes from, or undefined when the page is not part of one.
   *
   * Sitecore stores `frontEndRootPath` as a path string on every page of a flow.
   * Sanity stores a boolean marking the root document, which is a truth about
   * the document rather than an address, so the router cannot use it: it would
   * take `true` as its base path. The address is recoverable, because it is the
   * root's own slug under the container's base path, and the value is rebuilt
   * here rather than left to each consumer to work out.
   *
   * A root document names itself. A step cannot, so it is taken from the segment
   * that actually resolved it, which `findPage` reports rather than this
   * inferring it: a nested-looking path can also be a direct slug match, and a
   * document stored with a full slug like `artikelen/een-artikel` is not part of
   * a flow at all. Guessing from the path would hand it `/artikelen/` as a base
   * path.
   *
   * A flow root can also be the container's own home page, where the request
   * path strips to `/` and there is no segment to take. The address is still
   * known, because it is the base path itself.
   *
   * **Under the address model the root is read, not guessed.** `buildAddressPageQuery` projects
   * `frontEndRootPath` for the page and every ancestor (`_root0`, `_root1`, …, nearest first), so
   * the flow root is the nearest of those that is `true` — and its address is the request's own
   * segments truncated there. That is a whole-depth answer; the legacy branch below could only ever
   * describe a two-segment flow, because that is all its lookup could resolve.
   *
   * Trailing slash to match the shape Sitecore sends, which callers concatenate
   * a step onto (`docs/router.md`).
   */
  const toFlowRootPath = (resolution: PageResolution, requestPath: string): string | undefined => {
    const prefix = config.basePath ? toBasePathPrefix(config.basePath) : '';
    const root = prefix === '/' ? '' : prefix;
    const compose = (steps: readonly string[]): string =>
      steps.length === 0 ? `${root}/` : `${root}/${steps.join('/')}/`;

    if (resolution.segments) {
      const { segments } = resolution;
      // The root N ancestors up truncates the address to everything above it.
      const hop = flowRootHop(resolution.page, segments);
      return hop === undefined ? undefined : compose(segments.slice(0, segments.length - hop));
    }

    const segments = toSegments(requestPath);
    let rootSlug: string | undefined;
    // `?? ''` rather than a miss: the home page is a root with no segment of its own.
    if (resolution.page.frontEndRootPath === true) rootSlug = segments.at(-1) ?? '';
    else rootSlug = resolution.flowRootSlug;
    if (rootSlug === undefined) return undefined;

    return compose(rootSlug ? [rootSlug] : []);
  };

  /**
   * Split a block into flat `content` and nested slot `placeholders` — see `isSlotArray`.
   *
   * `fieldPath` is the GROQ selector locating this block inside its document, accumulated
   * as the recursion descends (`sections[_key=="a1"].sectionMain[_key=="b2"]`). Visual
   * Editing needs it to map a DOM node back to a field; it is inert otherwise. Passing
   * `undefined` (the default, and what the exported `toRendering` uses) skips the whole
   * business, since a caller mapping one detached block has no document to be relative to.
   */
  const toRendering = (block: SanityBlock, parentPath?: string): CmsRendering => {
    const content: Record<string, unknown> = {};
    const placeholders: Record<string, CmsRendering[]> = {};
    const placeholderPaths: Record<string, string> = {};

    for (const [key, value] of Object.entries(block)) {
      if (RESERVED.has(key)) continue;
      // Drop nulls (e.g. slot fields projected but absent on this document).
      if (value === null || value === undefined) continue;
      if (isSlotArray(value)) {
        const slotPath = parentPath === undefined ? undefined : joinPath(parentPath, key);
        placeholders[key] = value.map(item => toRendering(item, itemPath(slotPath, item)));
        // A nested slot field is exactly its array, so it is safe to drag.
        if (slotPath !== undefined) placeholderPaths[key] = slotPath;
        continue;
      }
      content[key] = value;
    }

    return {
      componentName: block._type,
      uid: block._key ?? block._id,
      content: toComponentContent(content),
      ...(parentPath !== undefined ? { fieldPath: parentPath } : {}),
      ...(Object.keys(placeholders).length > 0 ? { placeholders } : {}),
      ...(Object.keys(placeholderPaths).length > 0 ? { placeholderPaths } : {}),
    };
  };

  /**
   * Pool every top-level slot field's blocks together and reconstruct the tree from `parentKey`/`area`
   * rather than genuine field nesting — the shape the migration bundle's actual stored content uses (a
   * flat list mirroring Sitecore's own flat rendering-list-plus-placeholder-assignment representation),
   * confirmed against real dataset content: `Section`/`SplitView`/`TitleTextCTA`/etc. are siblings in one
   * array, each carrying `parentKey` (its logical container's `_key`; absent for a page-root block) and
   * `area` (the placeholder it belongs to under that container — literal Sitecore names like
   * `jss-section-main`, matching `slots.ts`'s candidate lists on the `@eneco/cms-components` side).
   *
   * A block with `area` but no resolvable `parentKey` is a page-root block, grouped under its own `area`.
   * A block with neither (older/simpler schemas — test fixtures, the POC schema) falls back to the
   * top-level field name it was pooled from, reproducing the pre-flat-pool behavior exactly.
   *
   * Called once per *independent* pool — see `buildTree`. `parentKey` only ever resolves within the pool
   * it was pooled from, which is what keeps a page's blocks and its chrome document's blocks from
   * parenting each other.
   */
  const groupPool = (page: SanityPage, fields: readonly string[], draft = false): Record<string, CmsRendering[]> => {
    const sourceFieldOf = new Map<SanityBlock, string>();
    const pool: SanityBlock[] = [];
    for (const field of fields) {
      const value = (page as Record<string, unknown>)[field];
      if (!Array.isArray(value)) continue;
      for (const item of value) {
        if (!isObject(item) || typeof item._type !== 'string') continue;
        pool.push(item as SanityBlock);
        sourceFieldOf.set(item as SanityBlock, field);
      }
    }

    const byKey = new Map<string, SanityBlock>();
    for (const block of pool) if (typeof block._key === 'string') byKey.set(block._key, block);

    // Draft mode only, and never for the shared chrome document: its blocks are projected onto
    // `chromeSections`, a name the page document does not have, so a reorder written against it would
    // patch a field that is not there.
    const reorderable = draft && !fields.includes(CHROME_SLOT_FIELD);

    const ROOT = '';
    const childrenOf = new Map<string, SanityBlock[]>();
    for (const block of pool) {
      const parentKey = asKey(block.parentKey);
      const bucket = parentKey !== undefined && byKey.has(parentKey) ? parentKey : ROOT;
      (childrenOf.get(bucket) ?? childrenOf.set(bucket, []).get(bucket)!).push(block);
    }

    // `parentKey` is migration output, so a cycle (A parents B, B parents A) is possible. Tracked by
    // identity rather than `_key` so a keyless block still counts exactly once, and consulted both to
    // stop `buildBlock` recursing forever and to find blocks no root can reach (below).
    const visited = new Set<SanityBlock>();

    const groupByArea = (blocks: SanityBlock[], parent: string): Record<string, CmsRendering[]> => {
      const groups: Record<string, CmsRendering[]> = {};
      for (const block of blocks) {
        if (visited.has(block)) continue;
        visited.add(block);
        const area =
          ((): string | undefined => {
            const area = asKey(block.area);
            return area && area.length > 0 ? area : undefined;
          })() ??
          sourceFieldOf.get(block);
        if (!area) continue; // every pooled block came from a named field, so this shouldn't happen — skip rather than guess.
        // The slot is the parent and the area within it. Two blocks of one slot render side by side in
        // array order, so reordering them in the array is a real reorder; a block moved past one of a
        // different slot would change nothing on screen, since its `parentKey` and `area` stay put.
        const slot = reorderable && typeof block._key === 'string' ? `${parent || 'root'}|${area}` : undefined;
        groups[area] ??= [];
        groups[area].push(buildBlock(block, undefined, slot));
      }
      return groups;
    };

    /**
     * Like `toRendering`, but also folds in this block's flat-pool children (`childrenOf`), grouped by area.
     *
     * A pooled block's address in the document is its slot in the top-level array it was pooled
     * from — `sections[_key=="…"]` — not its position in the tree reconstructed here. So the item
     * path comes from `sourceFieldOf`, and the areas folded in below get NO entry in
     * `placeholderPaths`: they are a grouping over one array, and reordering within one would
     * rewrite the entries of every other area sharing it. Genuinely nested fields on a pooled
     * block are still real arrays, and keep their drag target.
     */
    const buildBlock = (block: SanityBlock, explicitPath?: string, dragGroup?: string): CmsRendering => {
      const content: Record<string, unknown> = {};
      const placeholders: Record<string, CmsRendering[]> = {};
      const placeholderPaths: Record<string, string> = {};

      // Only a *pooled* block can be located via `sourceFieldOf` — it is the map of which
      // top-level array each one came out of. A block nested inside another block's own slot
      // field was never pooled, so its path has to be handed down by the parent instead.
      const sourceField = sourceFieldOf.get(block);
      const fieldPath = explicitPath ?? (sourceField === undefined ? undefined : itemPath(sourceField, block));

      for (const [key, value] of Object.entries(block)) {
        if (RESERVED.has(key)) continue;
        if (value === null || value === undefined) continue;
        if (isSlotArray(value)) {
          const slotPath = fieldPath === undefined ? undefined : joinPath(fieldPath, key);
          // A genuinely nested slot is one array of its own, so the array path is its own group.
          placeholders[key] = value.map(item =>
            buildBlock(
              item,
              itemPath(slotPath, item),
              reorderable && slotPath !== undefined && typeof item._key === 'string' ? slotPath : undefined,
            ),
          );
          if (slotPath !== undefined) placeholderPaths[key] = slotPath;
          continue;
        }
        content[key] = value;
      }

      const flatChildren = typeof block._key === 'string' ? childrenOf.get(block._key) : undefined;
      if (flatChildren) {
        for (const [area, renderings] of Object.entries(groupByArea(flatChildren, block._key as string))) {
          placeholders[area] = [...(placeholders[area] ?? []), ...renderings];
        }
      }

      const editFields = draft && fieldPath !== undefined ? collectEditFields(block, fieldPath, isSlotArray) : [];

      return {
        componentName: block._type,
        uid: block._key ?? block._id,
        content: toComponentContent(content),
        ...(fieldPath !== undefined ? { fieldPath } : {}),
        ...(Object.keys(placeholders).length > 0 ? { placeholders } : {}),
        ...(Object.keys(placeholderPaths).length > 0 ? { placeholderPaths } : {}),
        ...(editFields.length > 0 ? { editFields } : {}),
        ...(dragGroup !== undefined && fieldPath !== undefined ? { dragGroup } : {}),
      };
    };

    const rootPlaceholders = groupByArea(childrenOf.get(ROOT) ?? [], ROOT);

    // Both halves of a `parentKey` cycle are children of each other, so neither lands in ROOT and
    // neither would otherwise be built — the page would silently lose them. Sweep whatever the root
    // walk could not reach in at top level: an arbitrary resolution for corrupt data, but a lossless
    // one, and a missing block is far worse than a misplaced one.
    for (const [area, renderings] of Object.entries(groupByArea(pool.filter(block => !visited.has(block)), ROOT))) {
      rootPlaceholders[area] = [...(rootPlaceholders[area] ?? []), ...renderings];
    }

    return rootPlaceholders;
  };

  /**
   * The page's root placeholders, from its own slot fields plus its referenced chrome document.
   *
   * Two pools rather than one, because the dataset holds two generations of chrome at once: older pages
   * carry `Logo`/`MegaMenu`/`SubFooterMenu` inline in their own `sections`, newer ones reference a shared
   * `pageChrome` document (`queries.ts#CHROME_REFERENCE_FIELD`), and 19 of the GB pages have *both*.
   * Pooling them together would render those headers and footers twice.
   *
   * **A root area the page produces itself wins over the same area from the chrome document.** Stated
   * per-area rather than as "ignore chrome when the page has any", so a page carrying only half its
   * chrome inline still gets the other half — and stated without naming `jss-navigationbar`/`jss-footer`,
   * which are the components' business (`slots.ts`, `CmsLayout.tsx`), not the adapter's.
   */
  const buildTree = (
    page: SanityPage,
    draft = false,
  ): { content: Record<string, unknown>; placeholders?: Record<string, CmsRendering[]> } => {
    const rootPlaceholders = groupPool(page, slotFields, draft);
    for (const [area, renderings] of Object.entries(groupPool(page, [CHROME_SLOT_FIELD], draft))) {
      rootPlaceholders[area] ??= renderings;
    }

    // The page document's own scalar/object fields — everything except the pooled slot fields
    // (represented above via placeholders, not raw arrays) and reserved keys.
    const content: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(page)) {
      if (RESERVED.has(key) || isFlowRootProjection(key) || slotFields.includes(key) || key === CHROME_SLOT_FIELD)
        continue;
      if (value === null || value === undefined) continue;
      content[key] = value;
    }

    return { content: toComponentContent(content), ...(Object.keys(rootPlaceholders).length > 0 ? { placeholders: rootPlaceholders } : {}) };
  };

  /**
   * Map a page document onto the agnostic `CmsPage`.
   *
   * `draft` only adds the {@link CmsEditingContext} the render host needs to turn on Visual
   * Editing — the content itself is already draft or published by the time it gets here, decided
   * by which client fetched it. The editing context is omitted (and Visual Editing therefore
   * stays off) when `studioUrl` is unconfigured, because an overlay with nowhere to navigate is
   * worse than no overlay.
   *
   * `_id` is passed through as-is, `drafts.` prefix included: Sanity's own `createEditUrl`
   * normalizes it and uses the prefix to pick the perspective to open the document in.
   */
  const toPage = (raw: unknown, draft = false): CmsPage => {
    const page = raw as SanityPage;
    if (!isObject(page) || typeof page._type !== 'string') {
      throw new Error('Sanity adapter: cannot map a nullish/typeless page payload');
    }
    const ctx = page._context ?? {};
    const { content, placeholders } = buildTree(page, draft);
    const documentId = page._id;
    const editing =
      draft && config.studioUrl && typeof documentId === 'string'
        ? {
            studioUrl: config.studioUrl,
            workspace: config.studioWorkspace ?? config.dataset,
            documentId,
            documentType: page._type,
            disableDraftUrl: config.disableDraftUrl ?? defaultDisableDraftUrl(),
          }
        : undefined;
    return {
      context: {
        locale: (ctx.locale ?? 'nl-NL') as Locale,
        // Draft mode is an editing session in every sense this flag means, and several
        // components already dial back tracking/personalization when it is set.
        pageEditing: ctx.pageEditing ?? draft,
        pagePath: ctx.pagePath ?? '/',
        ...(ctx.customerId !== undefined ? { customerId: ctx.customerId } : {}),
        ...(ctx.experimentInfo?.name && ctx.experimentInfo.variant
          ? { experimentInfo: { name: ctx.experimentInfo.name, variant: ctx.experimentInfo.variant } }
          : {}),
        cmsSource: 'sanity',
        ...(editing ? { editing } : {}),
      },
      root: {
        componentName: page._type,
        uid: page._key ?? page._id,
        content,
        ...(placeholders ? { placeholders } : {}),
      },
    };
  };

  return {
    client: {
      getPage: async ({ path, locale, defaultLocale, draft = false }) => {
        const languageCandidates = toLanguageCandidates(locale);
        const result = await findPage(path, languageCandidates, locale, draft);
        if (!result) {
          // Name the scope that was actually applied — a lookup filtered out by an unlisted
          // document type looks identical to a genuinely missing page otherwise. The stripped
          // path is reported rather than the requested one, since that is what was searched.
          const scope = matchSite ? `site=${config.site}` : documentTypes.join('/');
          const requestPath = stripBasePath(path, config.basePath);
          // Typed so callers (resolveCmsPage.ts) can tell a genuine content miss apart
          // from a transport/config failure, which stays a plain Error.
          throw new CmsPageNotFoundError(
            `Sanity adapter: no ${scope} page for "${requestPath}" (${languageField} in ${languageCandidates.join('|')})`,
          );
        }
        // Replaced on the document rather than on the mapped page, so the fields travel the same
        // route into `content` as every other one. Derived here rather than in `toPage`, which sees
        // a document and not the request that found it, and a step needs that request to know which
        // flow it belongs to. The flow's name and its completion and cancel links come along, since
        // only its root stores them.
        const flowRootPath = toFlowRootPath(result, stripBasePath(path, config.basePath));
        const flowName = flowRootPath ? toFlowName(result) : undefined;
        const flowLinks = flowRootPath ? toFlowRootLinks(result) : {};
        // Every link with its URL, for this request: the page it is shown on decides whether a link
        // leaves its site, and the container's default locale which languages carry a path segment.
        const page = await resolveLinks(
          flowRootPath
            ? { ...result.page, frontEndRootPath: flowRootPath, ...(flowName ? { flowName } : {}), ...flowLinks }
            : result.page,
          { draft, host: config.host, defaultLocale },
        );
        return toPage(page, draft);
      },
      getPlaceholder: async ({ path, placeholderName, locale, defaultLocale }) => {
        const result = await findPage(path, toLanguageCandidates(locale), locale);
        if (!result) return [];
        const page = await resolveLinks(result.page, { host: config.host, defaultLocale });
        return toPage(page).root.placeholders?.[placeholderName] ?? [];
      },
    },
    toPage,
    toRendering: (raw: unknown) =>
      isMappedRendering(raw) ? fromMappedRendering(raw) : toRendering(raw as SanityBlock),
    toImage,
    toRichText,
  };
};
